import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import { Delete, Email, Close, CheckCircle } from "@mui/icons-material";
import { styled } from "@mui/material/styles";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 16,
    boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
    background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
  color: "white",
  padding: "20px 24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  "& .MuiTypography-root": {
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: "24px",
  "&::-webkit-scrollbar": {
    width: 8,
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
    borderRadius: 4,
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#c5c5c5",
    borderRadius: 4,
  },
}));

const ProductCard = styled(Box)(({ theme }) => ({
  padding: "12px 16px",
  marginBottom: "12px",
  borderRadius: "12px",
  border: "1px solid #e0e0e0",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: "white",
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    transform: "translateY(-1px)",
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
  color: "white",
  borderRadius: "12px",
  padding: "10px 20px",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(99, 102, 241, 0.2)",
  "&:hover": {
    boxShadow: "0 6px 16px rgba(99, 102, 241, 0.3)",
    background: "linear-gradient(135deg, #5a5feb 0%, #4a43d8 100%)",
  },
  "&:disabled": {
    background: "linear-gradient(135deg, #cccccc 0%, #aaaaaa 100%)",
    boxShadow: "none",
  },
}));

const OutlinedButton = styled(Button)(({ theme }) => ({
  border: "1px solid #e0e0e0",
  borderRadius: "12px",
  padding: "10px 20px",
  fontWeight: 500,
  textTransform: "none",
  color: "#64748B",
  "&:hover": {
    border: "1px solid #c5c5c5",
    background: "#f8f9fa",
  },
}));

const ResponseMessage = styled(Box)(({ success }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px 16px",
  borderRadius: "12px",
  marginTop: "16px",
  background: success 
    ? "linear-gradient(135deg, #10B981 0%, #059669 100%)" 
    : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
  color: "white",
  fontWeight: 500,
  animation: "fadeIn 0.3s ease",
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(-10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
}));

export default function Emailverifyprocess({
  open,
  onClose,
  selectedProducts,
  onRemoveProduct,
}) {
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  const handleVerify = async () => {
    if (!selectedProducts || selectedProducts.length === 0) {
      setResponseMessage("No records selected for verification.");
      return;
    }

    setLoading(true);
    setResponseMessage("");

    try {
      const payload = {
        records: selectedProducts.map((product) => ({
          record_id: product.id,
          event_name: product.event_name,
        })),
      };

 const res = await fetch(
  `/api/v1/data-varification/verify-email`,
  {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }
);


      const data = await res.json();

      if (res.ok) {
        setResponseMessage("Verification started successfully!");
      } else {
        setResponseMessage(`Failed: ${data?.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Verification Error:", error);
      setResponseMessage("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <Typography variant="h6">
          <Email sx={{ fontSize: 28 }} />
          Email Verification Process
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <Close />
        </IconButton>
      </StyledDialogTitle>
      

      <StyledDialogContent>
        <Typography variant="body1" gutterBottom sx={{ color: "#64748B", mb: 2 ,mt:7}}>
          Verify email addresses for the selected records. This process will check the validity and deliverability of each email.
        </Typography>

        {loading && <LinearProgress sx={{ height: 6, borderRadius: 3, mb: 2 }} />}

        {selectedProducts && selectedProducts.length > 0 ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "#374151" }}>
              Selected Records ({selectedProducts.length})
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: "auto", pr: 1 }}>
              {selectedProducts.map((product) => (
                <ProductCard key={product.id}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#1F2937" }}>
                      {product.event_name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                      ID: {product.id}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => onRemoveProduct(product.id)}
                    color="error"
                    size="small"
                    sx={{ 
                      background: "rgba(239, 68, 68, 0.1)",
                      "&:hover": { background: "rgba(239, 68, 68, 0.2)" }
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </ProductCard>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ 
            textAlign: "center", 
            py: 4, 
            background: "rgba(0,0,0,0.02)", 
            borderRadius: 2,
            border: "1px dashed #e0e0e0"
          }}>
            <Email sx={{ fontSize: 48, color: "#9CA3AF", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No records selected. Please select records to verify.
            </Typography>
          </Box>
        )}

        {responseMessage && (
          <ResponseMessage success={responseMessage.startsWith("Verification started")}>
            {responseMessage.startsWith("Verification started") ? (
              <CheckCircle sx={{ fontSize: 20 }} />
            ) : (
              <Delete sx={{ fontSize: 20 }} />
            )}
            {responseMessage}
          </ResponseMessage>
        )}
      </StyledDialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <OutlinedButton onClick={onClose}>
          Cancel
        </OutlinedButton>
        <GradientButton
          onClick={handleVerify}
          variant="contained"
          disabled={loading || !selectedProducts || selectedProducts.length === 0}
          startIcon={loading ? null : <Email />}
        >
          {loading ? "Verifying..." : "Start Verification"}
        </GradientButton>
      </DialogActions>
    </StyledDialog>
  );
}